/**
 * StudioDocumentTab — generic tab content for one Studio Document (Employee Handbook, Mission &
 * Core Values). Renders the current markdown body read-only for everyone; Principal tier gets an
 * Edit button opening a modal with MarkdownField. Deliberately content-free until Matt writes it —
 * see docs/STUDIO.md "Studio Documents" for why nothing is seeded here.
 */

import { useState } from 'react'
import Markdown from './Markdown.jsx'
import MarkdownField from './MarkdownField.jsx'
import { updateStudioDocument } from '../services/studioDocuments.js'
import { ctx } from '../outline/ctx.js'

export default function StudioDocumentTab({ doc, admin, onSaved }) {
  const [editing, setEditing] = useState(false)

  if (!doc) return <div style={S.empty}>Loading…</div>

  return (
    <div style={{ padding: '20px 24px 60px', maxWidth: 760, margin: '0 auto' }}>
      <div style={S.head}>
        <div style={S.title}>{doc.title}</div>
        {admin && <button style={S.editBtn} onClick={() => setEditing(true)}>Edit</button>}
      </div>
      {doc.updated_at && doc.body_markdown && (
        <div style={S.meta}>
          Last updated {new Date(doc.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          {doc.updated_by_name && <> by {doc.updated_by_name}</>}
        </div>
      )}
      {doc.body_markdown ? (
        <div style={S.body}><Markdown text={doc.body_markdown} /></div>
      ) : (
        <div style={S.empty}>
          Nothing written yet.{admin ? ' Click Edit to add it.' : ' Check back once a Principal has added it.'}
        </div>
      )}

      {editing && (
        <EditDocModal
          doc={doc}
          onClose={() => setEditing(false)}
          onSaved={updated => { setEditing(false); onSaved(updated) }}
        />
      )}
    </div>
  )
}

function EditDocModal({ doc, onClose, onSaved }) {
  const [title, setTitle] = useState(doc.title || '')
  const [body, setBody] = useState(doc.body_markdown || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    setSaving(true); setError('')
    try {
      const memberName = (ctx.currentMember && ctx.currentMember.fields && ctx.currentMember.fields['Member Name']) || undefined
      const updated = await updateStudioDocument(doc.slug, { title: title.trim() || doc.title, bodyMarkdown: body, updatedByName: memberName })
      onSaved(updated)
    } catch (e) {
      setError(e.message || 'Save failed.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={M.overlay} onClick={onClose}>
      <div style={M.card} onClick={e => e.stopPropagation()}>
        <div style={M.head}>
          <span style={M.title}>Edit {doc.title}</span>
          <button style={M.x} onClick={onClose}>&times;</button>
        </div>
        <div style={M.body}>
          {error && <div style={M.notice}>{error}</div>}
          <div style={M.field}>
            <div style={M.fieldLabel}>Title</div>
            <input style={M.input} value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div style={M.field}>
            <div style={M.fieldLabel}>Content</div>
            <MarkdownField value={body} onChange={setBody} minHeight={320} placeholder="Write it here — Markdown formatting supported." />
          </div>
        </div>
        <div style={M.foot}>
          <button style={M.ghost} onClick={onClose}>Cancel</button>
          <button style={M.primary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}

const S = {
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  title: { fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 22, letterSpacing: '0.02em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  editBtn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer' },
  meta: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', marginBottom: 18 },
  body: { fontFamily: '"DM Sans",sans-serif', fontSize: 14, lineHeight: 1.65, color: 'var(--aj-text)' },
  empty: { padding: '40px 0', textAlign: 'center', color: 'var(--aj-text-muted)', fontFamily: '"DM Sans",sans-serif', fontSize: 13 },
}

const M = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 700, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' },
  card: { width: '100%', maxWidth: 680, display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.3)', maxHeight: '90vh' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  title: { fontFamily: 'Montserrat,sans-serif', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)' },
  x: { background: 'none', border: 'none', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', cursor: 'pointer' },
  body: { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: '0.5px solid var(--aj-border-mid)', flexShrink: 0 },
  field: { display: 'flex', flexDirection: 'column' },
  fieldLabel: { fontFamily: '"DM Mono",monospace', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 },
  input: { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)' },
  primary: { background: '#07272D', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: '#fff' },
  ghost: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' },
  notice: { background: 'rgba(184,134,11,0.1)', border: '0.5px solid rgba(184,134,11,0.3)', borderRadius: 6, padding: '8px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#8a6508' },
}
